import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { incrementVersion } from "@brewbase/lib";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  PlusIcon,
  LockIcon,
  CopyIcon,
  ChevronDown,
  ChevronRight,
  ArchiveIcon,
  ArchiveRestoreIcon,
} from "lucide-react";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";
import type { ProcedureListItem } from "@/features/fermentation-procedure-editor";
import { z } from "zod";
import { useZodForm } from "@/components/form";
import { CultivationTypeEnum, EquipmentModelId } from "@brewbase/db/types";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";

export const Route = createFileRoute("/fermentations/procedures/")({
  component: RouteComponent,
});

const newProcedureSchema = z.object({
  name: z.string().min(1, "Name is required"),
  version: z.string().min(1, "Version is required"),
  description: z.string().optional(),
  cultivationType: z.nativeEnum(CultivationTypeEnum).optional(),
  equipmentModelId: z.coerce
    .number()
    .transform((val) => val as EquipmentModelId)
    .optional(),
});

function RouteComponent() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedProcedures, setExpandedProcedures] = useState<Set<string>>(
    new Set(),
  );
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [tab, setTab] = useState<"active" | "archived">("active");

  const { data: procedures, isLoading } =
    trpc.fermentationProcedures.list.useQuery({
      includeArchived: tab === "archived",
    });

  const { data: archivedCount } =
    trpc.fermentationProcedures.countArchived.useQuery();

  const { data: fermentorModels } =
    trpc.equipment.fermentors.models.list.useQuery();

  const createMutation = trpc.fermentationProcedures.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Procedure "${data.name}" created!`);
      utils.fermentationProcedures.list.invalidate();
      setIsNewDialogOpen(false);
      navigate({
        to: "/fermentations/procedures/$procedure_id/overview",
        params: { procedure_id: data.id },
      });
    },
    onError: (error) => {
      toast.error(`Failed to create procedure: ${error.message}`);
    },
  });

  const createNewVersionMutation =
    trpc.fermentationProcedures.createVersion.useMutation({
      onSuccess: (data) => {
        toast.success(`New version "${data.version}" created!`);
        utils.fermentationProcedures.list.invalidate();
        navigate({
          to: "/fermentations/procedures/$procedure_id/overview",
          params: { procedure_id: data.id },
        });
      },
      onError: (error) => {
        toast.error(`Failed to create new version: ${error.message}`);
      },
    });

  const deleteMutation = trpc.fermentationProcedures.delete.useMutation({
    onSuccess: () => {
      toast.success("Procedure deleted.");
      utils.fermentationProcedures.list.invalidate();
      utils.fermentationProcedures.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to delete procedure: ${error.message}`);
    },
  });

  const archiveMutation = trpc.fermentationProcedures.archive.useMutation({
    onSuccess: () => {
      toast.success("Procedure archived.");
      utils.fermentationProcedures.list.invalidate();
      utils.fermentationProcedures.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to archive: ${error.message}`);
    },
  });

  const unarchiveMutation = trpc.fermentationProcedures.unarchive.useMutation({
    onSuccess: () => {
      toast.success("Procedure unarchived.");
      utils.fermentationProcedures.list.invalidate();
      utils.fermentationProcedures.countArchived.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to unarchive: ${error.message}`);
    },
  });

  const form = useZodForm({
    schema: newProcedureSchema,
    defaultValues: {
      name: "",
      version: "1.0",
      description: "",
    },
  });

  const toggleExpanded = (procedureName: string) => {
    setExpandedProcedures((prev) => {
      const next = new Set(prev);
      if (next.has(procedureName)) {
        next.delete(procedureName);
      } else {
        next.add(procedureName);
      }
      return next;
    });
  };

  // Group procedures by name (for versioning)
  const groupedProcedures: Record<string, ProcedureListItem[]> = useMemo(() => {
    if (!procedures) return {};

    const filtered = procedures.filter((p) => {
      // Filter by tab: archived tab shows only archived, active tab shows only active
      const isArchived = !!p.archived_at;
      if (tab === "archived" && !isArchived) return false;
      if (tab === "active" && isArchived) return false;

      const searchLower = searchQuery.toLowerCase();
      return (
        p.name.toLowerCase().includes(searchLower) ||
        p.version.toLowerCase().includes(searchLower) ||
        (p.description || "").toLowerCase().includes(searchLower) ||
        (p.cultivation_type || "").toLowerCase().includes(searchLower)
      );
    });

    return filtered.reduce(
      (acc, procedure) => {
        const key = procedure.name;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(procedure);
        return acc;
      },
      {} as Record<string, ProcedureListItem[]>,
    );
  }, [procedures, searchQuery, tab]);

  const handleCreateProcedure = form.handleSubmit((values) => {
    createMutation.mutate({
      name: values.name,
      version: values.version,
      description: values.description || null,
      cultivationType: values.cultivationType || null,
      fermentorModelId: values.equipmentModelId
        ? (values.equipmentModelId as EquipmentModelId)
        : undefined,
    });
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Loading procedures...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search procedures..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Dialog open={isNewDialogOpen} onOpenChange={setIsNewDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="w-4 h-4 mr-1" />
              New Procedure
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Procedure</DialogTitle>
              <DialogDescription>
                Create a new fermentation procedure (SOP).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProcedure}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., DASGIP Fed-Batch"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      placeholder="e.g., 1.0"
                      {...form.register("version")}
                    />
                    {form.formState.errors.version && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.version.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cultivationType">Cultivation Type</Label>
                    <Select
                      value={form.watch("cultivationType") ?? ""}
                      onValueChange={(value) =>
                        form.setValue(
                          "cultivationType",
                          value as CultivationTypeEnum,
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="batch">Batch</SelectItem>
                        <SelectItem value="fed-batch">Fed-batch</SelectItem>
                        <SelectItem value="repeated fed-batch">
                          Repeated fed-batch
                        </SelectItem>
                        <SelectItem value="continuous">Continuous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fermentorModel">Fermentor Model</Label>
                    <Select
                      value={form.watch("equipmentModelId")?.toString() ?? ""}
                      onValueChange={(value) =>
                        form.setValue(
                          "equipmentModelId",
                          value.trim()
                            ? (parseInt(value) as EquipmentModelId)
                            : undefined,
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {fermentorModels?.map((model) => (
                          <SelectItem
                            key={model.id}
                            value={model.id.toString()}
                          >
                            {model.name} - {model.manufacturer}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description..."
                    {...form.register("description")}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsNewDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending
                    ? "Creating..."
                    : "Create Procedure"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <SlimTabs
        value={tab}
        onValueChange={(v) => setTab(v as "active" | "archived")}
        className="mb-4"
      >
        <SlimTabsList>
          <SlimTabsTrigger value="active">
            Active ({tab === "active" ? Object.values(groupedProcedures).flat().length : "..."})
          </SlimTabsTrigger>
          <SlimTabsTrigger value="archived">
            Archived ({archivedCount ?? 0})
          </SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>

      {/* Procedure list */}
      <div className="divide-y divide-border rounded-md border">
        {Object.entries(groupedProcedures)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, versions]) => {
            const hasMultipleVersions = versions.length > 1;
            const isExpanded = expandedProcedures.has(name);
            const latestVersion = versions[0]; // Assuming sorted by version desc

            // When searching or single version, show all
            const versionsToShow =
              searchQuery || !hasMultipleVersions
                ? versions
                : isExpanded
                  ? versions
                  : [latestVersion];

            return (
              <div key={name}>
                {/* Procedure group header (if multiple versions) */}
                {hasMultipleVersions && !searchQuery && (
                  <button
                    onClick={() => toggleExpanded(name)}
                    className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="font-medium">{name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {versions.length} versions
                    </span>
                  </button>
                )}

                {/* Version rows */}
                <div
                  className={hasMultipleVersions && !searchQuery ? "pl-6" : ""}
                >
                  {versionsToShow.map((procedure) => (
                    <div
                      key={procedure.id}
                      className="group flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Link
                            to="/fermentations/procedures/$procedure_id/overview"
                            params={{ procedure_id: procedure.id }}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {hasMultipleVersions || searchQuery
                              ? `v${procedure.version}`
                              : procedure.name}
                          </Link>

                          {procedure.is_locked && (
                            <Badge
                              variant="outline"
                              className="gap-1 text-warning border-warning"
                            >
                              <LockIcon className="h-3 w-3" />
                              Locked
                            </Badge>
                          )}

                          {procedure.cultivation_type && (
                            <Badge variant="secondary">
                              {procedure.cultivation_type}
                            </Badge>
                          )}

                          {procedure.fermentor_model_id && fermentorModels && (
                            <Badge
                              variant="outline"
                              className="text-muted-foreground"
                            >
                              {fermentorModels.find(
                                (m) => m.id === procedure.fermentor_model_id,
                              )?.name ?? "Unknown"}
                            </Badge>
                          )}
                        </div>

                        {procedure.description && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {procedure.description}
                          </p>
                        )}

                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>
                            Created {formatDate(procedure.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {tab === "active" ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                createNewVersionMutation.mutate({
                                  sourceProcedureId: procedure.id,
                                  newVersion: incrementVersion(procedure.version),
                                })
                              }
                              disabled={createNewVersionMutation.isPending}
                              title="Create new version"
                            >
                              <CopyIcon className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => archiveMutation.mutate(procedure.id)}
                              disabled={archiveMutation.isPending}
                              title="Archive"
                            >
                              <ArchiveIcon className="h-3 w-3" />
                            </Button>
                            <DeleteConfirmDialog
                              title="Delete Procedure"
                              description={`Delete "${procedure.name} v${procedure.version}"? This cannot be undone if the procedure has been used in a fermentation.`}
                              onConfirm={() => deleteMutation.mutate(procedure.id)}
                              isDeleting={
                                deleteMutation.isPending &&
                                deleteMutation.variables === procedure.id
                              }
                              disabled={procedure.is_locked}
                            />
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => unarchiveMutation.mutate(procedure.id)}
                              disabled={unarchiveMutation.isPending}
                              title="Unarchive"
                            >
                              <ArchiveRestoreIcon className="h-3 w-3" />
                            </Button>
                            <DeleteConfirmDialog
                              title="Delete Procedure"
                              description={`Delete "${procedure.name} v${procedure.version}"? This cannot be undone.`}
                              onConfirm={() => deleteMutation.mutate(procedure.id)}
                              isDeleting={
                                deleteMutation.isPending &&
                                deleteMutation.variables === procedure.id
                              }
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        {/* Empty state */}
        {Object.keys(groupedProcedures).length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-3 text-sm">
              {searchQuery
                ? "No procedures found matching your search."
                : "No procedures yet. Create your first one!"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
